import { useEffect, useMemo, useState } from 'react';
import { useAtom } from 'ximple';
import { NativeCalculateResult, NativeReserveCalculateRequest } from '@repo/types';
import {
    getProjectsFromCart,
    getToursFromCart,
    nativeProjectLineKey,
    nativeTourLineKey,
} from '@repo/widget-utils/ProductMapper';
import { nativeReserveCalculate } from '@repo/widget-utils/services/fetchers/native-membership';
import { useMembershipMode } from '@repo/widget-utils/widgetsConfiguration';
import { createInitialCheckoutInfoData } from '@repo/widget-utils/checkout-info-helper';
import { nativeMemberTokenAtom } from 'src/state/native-membership/nativeMemberTokenAtom';
import { useBookingContext } from 'src/widgets/BookingContext';

const DEBOUNCE_MS = 400;

export type NativeBookingCoverage = {
    // The full calculate result (for per-line display); null when no coverage applies.
    result: NativeCalculateResult | null;
    // Base's discounted grand total, but only when a member card actually covers something — so an
    // ineligible/expired member falls through to the gross price instead of an identical override.
    discountedTotal: number | null;
    isLoading: boolean;
};

/**
 * Native member-card coverage preview for the pre-cart booking card ("Bestill nå"). Builds the
 * calculate body from BookingContext.newCartItem (the derived, not-yet-carted CartItem) via the SAME
 * mappers the cart-wide effect uses, so the preview's keys match the eventual reserve body. Uses local
 * state (NOT the shared cart atom) — the configured booking isn't the cart, each card needs its own
 * preview, and it must not race the app-level cart coverage effect.
 *
 * A no-op for guest/legacy, accommodation, and packages (no tours/projects → request null → result
 * null). Fail-soft to gross on error: the checkout re-validates coverage authoritatively.
 */
export function useNativeBookingCoverage(): NativeBookingCoverage {
    const isNative = useMembershipMode() === 'native';
    const [token] = useAtom(nativeMemberTokenAtom);
    const { newCartItem } = useBookingContext();

    const active = isNative && Boolean(token) && newCartItem !== null;

    // Build + serialize in one memo: only re-fetch when the request body actually changes (changing
    // pax/date produces a new body; an unrelated re-render does not). The body keeps only id/key/pax,
    // so a placeholder checkoutInfo yields the same request the real reserve will send.
    const requestKey = useMemo(() => {
        if (!active || !newCartItem) return null;
        const ci = createInitialCheckoutInfoData(null, [newCartItem], null);
        if (!ci) return null;

        const tours = getToursFromCart([newCartItem], ci).map((tour) => ({
            id: tour.id,
            key: nativeTourLineKey(tour),
            pax: tour.pax.map((p) => ({ id: p.id, quantity: p.quantity })),
        }));
        const projects = getProjectsFromCart([newCartItem]).map((project) => ({
            id: project.id,
            key: nativeProjectLineKey(project),
            timeslots: project.timeslots,
            tickets: project.tickets,
        }));
        if (tours.length === 0 && projects.length === 0) return null;

        const request: NativeReserveCalculateRequest = {
            tours: tours.length > 0 ? tours : undefined,
            projects: projects.length > 0 ? projects : undefined,
        };
        return JSON.stringify(request);
    }, [active, newCartItem]);

    const [result, setResult] = useState<NativeCalculateResult | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!requestKey || !token) {
            setResult(null);
            setIsLoading(false);
            return;
        }
        const req = JSON.parse(requestKey) as NativeReserveCalculateRequest;
        let cancelled = false;
        setIsLoading(true);
        const handle = setTimeout(async () => {
            try {
                const r = await nativeReserveCalculate(req, token);
                if (!cancelled) {
                    setResult(r);
                    setIsLoading(false);
                }
            } catch {
                if (!cancelled) {
                    setResult(null); // fail soft to gross
                    setIsLoading(false);
                }
            }
        }, DEBOUNCE_MS);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [requestKey, token]);

    const discountedTotal = useMemo(() => {
        const memberCard = result?.member_card;
        if (!memberCard) return null;
        const hasCoverage = memberCard.per_line.some((line) => line.discount > 0);
        return hasCoverage ? result.total : null;
    }, [result]);

    return { result, discountedTotal, isLoading };
}
